import type { UserRole } from '@/api/users';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_PERMISSIONS, PERMISSION_MODULES, getPermissionsByModule } from './mock-data';
import type { PermissionModule } from './types';

/**
 * Backend-defined roles (fixed enum). These cannot be created or deleted
 * from the frontend -- they are defined in the backend RBAC system.
 */
interface RoleDefinition {
  code: UserRole;
  nameKey: string;
  descriptionKey: string;
  level: number; // RBAC hierarchy level (1-8)
  permissions: string[]; // Permission IDs for display
}

const BACKEND_ROLES: RoleDefinition[] = [
  {
    code: 'OPERATOR',
    nameKey: 'user.roleLabels.OPERATOR',
    descriptionKey: 'user.roleDescriptions.OPERATOR',
    level: 1,
    permissions: [
      'p-001',
      'p-002',
      'p-020',
      'p-030',
      'p-040',
      'p-041',
      'p-042',
      'p-050',
      'p-051',
      'p-053',
    ],
  },
  {
    code: 'TECHNIKUS',
    nameKey: 'user.roleLabels.TECHNIKUS',
    descriptionKey: 'user.roleDescriptions.TECHNIKUS',
    level: 2,
    permissions: ['p-001', 'p-002', 'p-020', 'p-030', 'p-060', 'p-061', 'p-062', 'p-063'],
  },
  {
    code: 'BOLTVEZETO',
    nameKey: 'user.roleLabels.BOLTVEZETO',
    descriptionKey: 'user.roleDescriptions.BOLTVEZETO',
    level: 3,
    permissions: [
      'p-001',
      'p-002',
      'p-010',
      'p-011',
      'p-012',
      'p-020',
      'p-021',
      'p-022',
      'p-023',
      'p-030',
      'p-031',
      'p-032',
      'p-033',
      'p-040',
      'p-041',
      'p-042',
      'p-043',
      'p-050',
      'p-051',
      'p-052',
      'p-053',
      'p-060',
      'p-061',
      'p-062',
      'p-063',
      'p-070',
      'p-071',
      'p-072',
      'p-073',
      'p-080',
    ],
  },
  {
    code: 'ACCOUNTANT',
    nameKey: 'user.roleLabels.ACCOUNTANT',
    descriptionKey: 'user.roleDescriptions.ACCOUNTANT',
    level: 4,
    permissions: [
      'p-001',
      'p-020',
      'p-030',
      'p-050',
      'p-051',
      'p-052',
      'p-070',
      'p-071',
      'p-072',
      'p-073',
    ],
  },
  {
    code: 'PARTNER_OWNER',
    nameKey: 'user.roleLabels.PARTNER_OWNER',
    descriptionKey: 'user.roleDescriptions.PARTNER_OWNER',
    level: 5,
    permissions: [
      'p-001',
      'p-010',
      'p-011',
      'p-012',
      'p-020',
      'p-021',
      'p-022',
      'p-023',
      'p-030',
      'p-031',
      'p-032',
      'p-033',
      'p-040',
      'p-041',
      'p-042',
      'p-043',
      'p-050',
      'p-051',
      'p-052',
      'p-053',
      'p-060',
      'p-061',
      'p-062',
      'p-063',
      'p-070',
      'p-071',
      'p-072',
      'p-073',
      'p-080',
    ],
  },
  {
    code: 'CENTRAL_ADMIN',
    nameKey: 'user.roleLabels.CENTRAL_ADMIN',
    descriptionKey: 'user.roleDescriptions.CENTRAL_ADMIN',
    level: 6,
    permissions: MOCK_PERMISSIONS.map(p => p.id),
  },
  {
    code: 'DEVOPS_ADMIN',
    nameKey: 'user.roleLabels.DEVOPS_ADMIN',
    descriptionKey: 'user.roleDescriptions.DEVOPS_ADMIN',
    level: 7,
    permissions: MOCK_PERMISSIONS.map(p => p.id),
  },
  {
    code: 'SUPER_ADMIN',
    nameKey: 'user.roleLabels.SUPER_ADMIN',
    descriptionKey: 'user.roleDescriptions.SUPER_ADMIN',
    level: 8,
    permissions: MOCK_PERMISSIONS.map(p => p.id),
  },
];

export function RoleManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<RoleDefinition | null>(BACKEND_ROLES[0] ?? null);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/users')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('user.rolesAndPermissions')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('user.rbacDescription')}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Info banner */}
        <Card className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">{t('user.rbacInfoBanner')}</p>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: Role list */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('user.rolesCount', { count: BACKEND_ROLES.length })}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {BACKEND_ROLES.map(role => (
                    <button
                      key={role.code}
                      onClick={() => setSelectedRole(role)}
                      className={`w-full p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-[#0f3d47] ${
                        selectedRole?.code === role.code
                          ? 'bg-primary/5 border-l-4 border-primary'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {t(role.nameKey)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{role.code}</p>
                        </div>
                        <div className="text-right">
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-[#1f5f6b] dark:text-[#99f6e4]">
                            {t('user.level', { level: role.level })}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Role details */}
          <div className="lg:col-span-2 space-y-6">
            {selectedRole ? (
              <>
                {/* Role info */}
                <Card>
                  <CardHeader>
                    <div>
                      <CardTitle>{t(selectedRole.nameKey)}</CardTitle>
                      <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {t(selectedRole.descriptionKey)}
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-[#ccfbf1]">{t('user.code')}</span>
                        <span className="ml-2 font-medium font-mono">{selectedRole.code}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('user.hierarchyLevel')}
                        </span>
                        <span className="ml-2 font-medium">{selectedRole.level} / 8</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('user.permissionsLabel')}
                        </span>
                        <span className="ml-2 font-medium">{selectedRole.permissions.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('user.typeLabel')}
                        </span>
                        <span className="ml-2 font-medium">{t('user.systemRole')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Permissions (read-only) */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t('user.permissionsCount', { count: selectedRole.permissions.length })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {PERMISSION_MODULES.map(module => {
                        const modulePermissions = getPermissionsByModule(
                          module.value as PermissionModule
                        );
                        const grantedCount = modulePermissions.filter(p =>
                          selectedRole.permissions.includes(p.id)
                        ).length;

                        return (
                          <div
                            key={module.value}
                            className="rounded-lg border p-4 dark:border-[#2d7d8a]"
                          >
                            <div className="mb-3 flex items-center justify-between">
                              <h3 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                                <span>{module.icon}</span>
                                {t(module.labelKey)}
                                <span className="text-sm font-normal text-gray-500 dark:text-[#99f6e4]">
                                  ({grantedCount}/{modulePermissions.length})
                                </span>
                              </h3>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {modulePermissions.map(permission => {
                                const isGranted = selectedRole.permissions.includes(permission.id);

                                return (
                                  <div
                                    key={permission.id}
                                    className={`rounded-lg border p-3 ${
                                      isGranted
                                        ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                                        : 'border-gray-200 bg-gray-50 opacity-50 dark:border-[#2d7d8a] dark:bg-[#1f5f6b]'
                                    }`}
                                  >
                                    <p
                                      className={`font-medium ${isGranted ? 'text-green-800 dark:text-green-300' : 'text-gray-500 dark:text-[#ccfbf1]'}`}
                                    >
                                      {t(permission.nameKey)}
                                    </p>
                                    <p
                                      className={`text-sm ${isGranted ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-[#99f6e4]'}`}
                                    >
                                      {t(permission.descriptionKey)}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500 dark:text-[#ccfbf1]">{t('user.selectRolePrompt')}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
